import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { post, put } from '../Services/http';
import { EditorStringModel } from '../Types/EditorStringModel';
import {
  InstallationData,
  InstallationType,
  MaterialCalculatorFloor,
  MaterialCalculatorRoom,
} from '../Types/MaterialCalculator';
import Product from '../Types/Product';
import { uniqueId } from 'lodash-es';

type MaterialCalculatorStateErrors = { [key: string]: string[] };

interface MaterialCalculatorState {
  title: string;
  description?: string;
  disclaimerDescription?: EditorStringModel;
  moreInfoTitle?: string;
  moreInfoDescription?: EditorStringModel;
  errors: MaterialCalculatorStateErrors;
  result?: MaterialCalculatorResultState;
  floors?: MaterialCalculatorFloor[];
  installationTypes?: InstallationType[];
  productsLoading?: boolean;
  error?: boolean;
  shunt: boolean;
  defaultDistanceToDistributor?: number;
}

interface MaterialCalculatorResultState {
  products?: Product[];
  floors?: MaterialCalculatorFloor[];
}

const DEFAULT_STATE: MaterialCalculatorState = {
  title: '',
  errors: {},
  shunt: true,
};

const getDefaultRoom = (installationType?: InstallationType) => ({
  id: uniqueId(),
  area: 0,
  installationType: installationType,
  installationData: {
    cc: installationType?.availableCCs.length
      ? installationType.availableCCs[0]
      : undefined,
  },
});

const getDefaultFloor = (rooms: MaterialCalculatorRoom[]) => ({
  id: uniqueId(),
  rooms: rooms,
});

export const getProducts = createAsyncThunk(
  'materialCalculator/getProducts',
  async (model: MaterialCalculatorState) => {
    const response = await post('/api/materialCalculator', model);
    return await response.json();
  }
);

const materialCalculatorSlice = createSlice({
  name: 'materialCalculator',
  initialState: DEFAULT_STATE,
  reducers: {
    setFloors: (state) => {
      const floors = state.floors;
      const firstInstallationType = state.installationTypes
        ? state.installationTypes[0]
        : undefined;
      floors?.push(getDefaultFloor([getDefaultRoom(firstInstallationType)]));
    },
    setRooms: (
      state,
      action: PayloadAction<{ floorIndex: number; floorId: string }>
    ) => {
      if (state.floors) {
        const floor = state.floors.find(
          (floor) => floor.id == action.payload.floorId
        );
        if (floor) {
          const firstInstallationType = state.installationTypes
            ? state.installationTypes[0]
            : undefined;
          floor.rooms?.push(getDefaultRoom(firstInstallationType));
        }
      }
    },
    removeFloor: (state, action: PayloadAction<{ floorIndex: number }>) => {
      state.floors?.splice(action.payload.floorIndex, 1);
    },
    removeRoom: (
      state,
      action: PayloadAction<{ floorIndex: number; roomIndex: number }>
    ) => {
      if (state.floors) {
        const floor = state.floors[action.payload.floorIndex];
        if (floor?.rooms) {
          floor.rooms.splice(action.payload.roomIndex, 1);
        }
      }
    },
    setRoomArea: (
      state,
      action: PayloadAction<{
        floorIndex: number;
        roomIndex: number;
        area: number;
      }>
    ) => {
      if (state.floors) {
        const floor = state.floors[action.payload.floorIndex];
        if (floor?.rooms) {
          const room = floor.rooms[action.payload.roomIndex];
          if (room) {
            room.area = action.payload.area;
          }
        }
      }
    },
    setRoomInstallationType: (
      state,
      action: PayloadAction<{
        floorIndex: number;
        roomIndex: number;
        installationType: InstallationType;
      }>
    ) => {
      if (state.floors) {
        const floor = state.floors[action.payload.floorIndex];
        if (floor?.rooms) {
          const room = floor.rooms[action.payload.roomIndex];
          if (room) {
            const installationData = room.installationData ?? {};
            installationData.cc = action.payload.installationType.availableCCs
              ?.length
              ? action.payload.installationType.availableCCs[0]
              : undefined;
            room.installationType = action.payload.installationType;
            room.installationData = installationData;
          }
        }
      }
    },
    setRoomInstallationData: (
      state,
      action: PayloadAction<{
        floorIndex: number;
        roomIndex: number;
        data: any;
        key: string;
      }>
    ) => {
      if (state.floors) {
        const floor = state.floors[action.payload.floorIndex];
        if (floor?.rooms) {
          const room = floor.rooms[action.payload.roomIndex];
          if (room) {
            if (!room.installationData) {
              room.installationData = {};
            }
            room.installationData[action.payload.key] = action.payload.data;
          }
        }
      }
    },
    setShunt: (state, action: PayloadAction<{ shunt: boolean }>) => {
      state.shunt = action.payload.shunt;
    },
    setError: (state, action: PayloadAction<{ error: boolean }>) => {
      state.error = action.payload.error;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProducts.fulfilled, (state, action) => {
      state.result = action.payload;
      state.floors = action.payload.floors;
      state.productsLoading = false;
      state.error = false;
    });
    builder.addCase(getProducts.rejected, (state, action) => {
      state.productsLoading = false;
      state.error = true;
    });
    builder.addCase(getProducts.pending, (state, action) => {
      state.productsLoading = true;
      state.error = false;
    });
  },
});

export const materialCalculatorActions = materialCalculatorSlice.actions;

export default materialCalculatorSlice.reducer;
